<template>
    <div>
        <multiselect
            :multiple="allowMultiple"
            :options="computedContacts"
            placeholder="Select a contact"
            label="full_name"
            track-by="tra_id"
            select-label=""
            :deselectLabel="allowMultiple ? 'Unselect' : ''"
            v-model="selectedContacts"
            ref="contactSelect"
            :close-on-select="true"
            :internal-search="false"
            @open="onOpen"
            @close="onClose"
            @search-change="onSearch"
        >
            <template #tag="{ option, remove }">
                <div class="inline-flex items-center bg-gray-100 rounded-md mr-2 mb-2 group hover:bg-gray-200 transition-colors duration-200">
                    <span class="py-1 px-2 text-gray-800">{{ option.full_name }}</span>
                    <button 
                        @click="removeSelectedContact(option)"
                        class="h-full px-2 py-2 border-l border-gray-300 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-colors duration-200 flex items-center justify-center rounded-r-md"
                        type='button'
                    >
                        <font-awesome-icon 
                            :icon="['fas', 'xmark']" 
                            class="w-3 h-3"
                        />
                    </button>
                </div>
            </template>
            
            <template #option="props">
                <div class="flex items-center justify-between py-0 px-2 transition-colors duration-200">
                    <div class="flex-1">
                        <template v-if="props.option.first_name || props.option.last_name">
                            <h3 class="text-gray-700 font-medium m-0">{{ props.option.first_name }} {{ props.option.last_name }}</h3>
                            <span class="text-gray-500 text-sm">{{ props.option.phone_number }}</span>
                        </template>
                        <template v-else>
                            <span class="text-gray-700 text-base">{{ props.option.phone_number }}</span>
                        </template>
                    </div>
                    <div 
                        v-if="isBadContact(props.option)"
                        class="flex items-center bg-red-50 text-red-700 px-3 py-0 rounded-md ml-4"
                    >
                        <font-awesome-icon
                            :icon="['far', 'exclamation-triangle']"
                            class="w-4 h-4"
                        />
                        <span class="ml-2 text-sm font-medium whitespace-nowrap">
                            {{ badContactReason(props.option) }}
                        </span>
                    </div>
                </div>
            </template>

            <template #beforeList>
                <button class="p-3 bg-blue-100 w-full" @click.prevent="showContactModal" type='button'>
                <font-awesome-icon :icon="['far', 'plus-circle']" ></font-awesome-icon>
                    Add new contact
                </button>
            </template>
                
            <template #afterList>
                <li ref="load" class="loader">
                    <div class="m-2">
                        <template v-if="hasNextPage">
                            Loading more contacts...
                        </template>
                        <template v-else-if="!searchText">
                            End of contacts
                        </template>
                    </div>
                </li>
            </template>
        </multiselect>

        <contact-create-modal
            v-if="addContactModalIsOpen"
            :is-open="addContactModalIsOpen"
            @contact-created="clearAndGetContacts"
            @close="addContactModalIsOpen = false"
        ></contact-create-modal>
    </div>
</template>

<script>

import JetDialogModal from "../Jetstream/DialogModal";
import JetSecondaryButton from "../Jetstream/SecondaryButton";

import _ from "lodash";
import ContactCreateModal from "./ContactCreateModal";
import axios from "axios";


export default {
    props: {
        modelValue: {
            type: [Object,Array],
        },
        allowMultiple: {
            type: Boolean,
            default: true
        }
    },
    emits: ['update:modelValue', 'contact-created'],
    components: {
        ContactCreateModal,
        //vSelect,
        JetDialogModal,
        JetSecondaryButton,
    },
    data() {
        return {
            contacts: [],
            nextPageUrl: null,
            selectedContacts: [],
            addContactModalIsOpen: false,
            observer: null,
            limit: 25,
            searchText: "",
            loading: false,
            contactId: null,
            observerOptions: {
                root: null, // Use the browser viewport as the root
                rootMargin: "20px", // margin around the root
                threshold: .01 // Trigger the callback when 1% of the target element is visible
            },
            contactSelectState: 'closed',
            searchMode:false, // used to clear contacts when switching from searching to not searching
        };
    },
    async mounted() {
        let params = new URLSearchParams(document.location.search);
        if (params.has("contact_id")) {
          this.contactId = params.get("contact_id")
        }
        if (this.contactId !== null) {
          this.getSelectedContact(this.contactId);
        } else {
          this.getContacts();
        }

        if (this.value !== null ) {
            if (this.value.length > 0 /* should happen if array */) {
                this.selectedContacts = this.value;
            } else if (this.value.phone_number /* should happen if object */) {
                this.selectedContacts = [this.value];
            }
        }
    },
    computed: {
        hasNextPage() {
            return this.nextPageUrl !== null;
        },
        computedContacts() {
            return this.contacts.map((contact) => {
                return {
                    ...contact,
                    $isDisabled: this.isBadContact(contact)
                };
            });
        },
        value() {
            return this.modelValue;
        }
    },

    methods: {
        disconnectObserver() {
            if (!this.observer) return;
            
            this.observer.disconnect();
        },
        async startObserver () {
            if (this.contactSelectState == 'closed') {
                return;
            }
            if (this.observer) {
                this.disconnectObserver();
            }

            await this.$nextTick();
            if (!this.$refs.load) {
                return;
            }
            this.observer = new IntersectionObserver(this.infiniteScroll, this.observerOptions);
            this.observer.observe(this.$refs.load);
        },
        onClose() {
            this.contactSelectState = 'closed';
            this.disconnectObserver();
        },
        async onOpen() {
            this.contactSelectState = 'open';
            if (this.hasNextPage) {
                this.startObserver();
            }
        },
        isBadContact(contact) {
            if(!contact) return false;
            return (
                contact.bad_contact ||
                contact.do_not_contact
            )
        },
        badContactReason(contact) {
            if(contact.do_not_contact) {
                return "Contact has opted out";
            }
            
            if (contact.bad_contact) {

                if (
                    contact.bad_contact_reason.includes("not a valid phone number") ||
                    contact.bad_contact_reason.includes("not a mobile number")

                ) {
                    return "Not a mobile phone number.";
                }
                if (
                    contact.bad_contact_reason.includes("unsubscribed") ||
                    contact.bad_contact_reason.includes("blacklist")
                ) {
                    return "Contact unsubscribed.";
                }

                if (contact.bad_contact_reason.includes("not been enabled for the region") ) {
                    return "Permission to send text messages is not enabled for this contact's region.";
                }

                // return generic reason
                return "Contact is marked not valid";
            }
        },
        async infiniteScroll(entries) {
            let isIntersecting = entries[0].isIntersecting;
            let target = entries[0].target;

            if (isIntersecting && !this.loading && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.getContacts(this.nextPageUrl, this.searchText);
                await this.$nextTick();
                ul.scrollTop = scrollTop;
            }
        },
        async clearAndGetContacts(contactId) {
            this.contacts = [];
            this.getContacts();
            this.$emit('contact-created', contactId);
        },
        async getContacts(url = null, search = null) {
            this.loading = true;
            await axios
                .get(url ? url : route("api.get.contacts"), {
                    params: {
                        search: search
                    }
                })
                .then((response) => {
                    if (this.searchMode != !!search) {
                        this.contacts = [];
                        this.searchMode = !!search;
                    }
                    this.nextPageUrl = response.data.next_page_url;
                    this.contacts.push(...response.data.data);

                })
                .catch(function (error) {
                    console.log(error);
                });
            this.loading = false;
            this.startObserver();
        },
        async getSelectedContact(contactId) {
          this.loading = true;
          await axios
              .get(route("api.find.contact", {
                id: contactId
              }))
              .then((response) => {
                  this.selectedContacts.push(response.data);
                  this.$emit('update:modelValue', this.selectedContacts);
              })
              .catch(function (error) {
                  console.log(error);
              });
          this.loading = false;
          this.startObserver();
        },
        onSearch(search, loading) {
            this.searchText = search;

            if( search.length ) {
                this.searchContacts(search, loading, this);
            } else {
                this.contacts = [];
                this.getContacts();
            }
        },
        searchContacts: _.debounce((search, loading, vm) => {
            vm.contacts = [];
            vm.getContacts(null, search);
        }, 350),
        showContactModal() {
            console.log("Show contact modal clicked")
            this.$refs.contactSelect.deactivate();
            this.addContactModalIsOpen = true;
        },
        selectOptionLabel( option ) {
            return `${option.name} <br/>${option.phone}`;
        },
        removeSelectedContact(contact) {
            this.selectedContacts = this.selectedContacts.filter((c) => c.tra_id !== contact.tra_id);
        }
    },
    watch: {
        selectedContacts(val) {
            this.$emit('update:modelValue', val)
        },
        value(val) {
            if (val !== null ) {
                if (val.length > 0 /* should happen if array */) {
                    this.selectedContacts = val;
                } else if (val.phone_number /* should happen if object */) {
                    this.selectedContacts = [val];
                }
            }
        }
    }
};
</script>
<style>
    .multiselect__option--highlight {
        @apply !bg-blue-100;
        /* background: #f9fafb !important; */
        color: inherit !important;
    }

    .multiselect__element:has(.multiselect__option--disabled) {
            cursor: not-allowed !important;
    }

    .multiselect__option--highlight:after {
        @apply !bg-blue-500;
    }
    /* Red for removal when already selected */
    .multiselect__option--selected.multiselect__option--highlight:after {
        @apply !bg-red-500;
    }

    .multiselect__option:not(:hover) {
        background: transparent !important;
    }
    .multiselect__option:not(:hover) .multiselect__option--highlight:after,
    .multiselect__option:not(:hover):after {
        @apply !bg-transparent;
    }

</style>
